import { useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtils/TextInput'
import FbButton from 'components/FbUI/FbButton'
import { handleFormError } from 'utils/handleFormError'

import {
  AIGeneratedSequence,
  CreateSequencePayload,
  useCreateSequence,
} from '../sequences_api'
import {
  SequenceForm,
  SequenceStepFormItem,
  defaultStep,
} from './SequenceStepFormItem'

interface CreateSequenceModalProps {
  campaignId: number
  isOpen: boolean
  onClose: () => void
  /** Optional initial values to pre-fill the form (e.g., from AI generation) */
  initialValues?: AIGeneratedSequence | null
}

const emptyDefaultValues: SequenceForm = {
  title: '',
  steps: [{ ...defaultStep }],
}

export function CreateSequenceModal({
  campaignId,
  isOpen,
  onClose,
  initialValues,
}: CreateSequenceModalProps) {
  const createSequenceMutation = useCreateSequence()

  // Convert AI generated sequence to form values
  const defaultValues = useMemo<SequenceForm>(() => {
    if (!initialValues) {
      return emptyDefaultValues
    }

    return {
      title: initialValues.title,
      steps: initialValues.steps.map((step) => ({
        email_subject: step.email_subject,
        email_body: step.email_body,
        wait_in_days: step.wait_in_days,
      })),
    }
  }, [initialValues])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
    watch,
  } = useForm<SequenceForm>({
    defaultValues,
    mode: 'onChange',
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'steps',
  })

  const watchedSteps = watch('steps')

  // Reset form when modal opens/closes or when initialValues change
  useEffect(() => {
    if (isOpen) {
      reset(defaultValues)
    } else {
      reset(emptyDefaultValues)
    }
  }, [isOpen, defaultValues, reset])

  const onSubmit = handleSubmit(async (values) => {
    const payload: CreateSequencePayload = {
      campaign_id: campaignId,
      title: values.title,
      steps: values.steps.map((step, index) => ({
        order: index + 1,
        email_subject: step.email_subject,
        email_body: step.email_body,
        wait_in_days: Math.max(1, step.wait_in_days),
      })),
    }

    const [err] = await to(createSequenceMutation.mutateAsync(payload))

    if (err) {
      handleFormError(err, setError)
      return
    }

    toast.success('Sequence created successfully')
    onClose()
  })

  const addStep = () => {
    append({ ...defaultStep })
  }

  const canSubmit = isValid && watchedSteps.length > 0

  const modalTitle = initialValues
    ? 'Review AI-Generated Sequence'
    : 'Create Email Sequence'
  const modalDescription = initialValues
    ? 'Review and edit the AI-generated sequence before creating it.'
    : 'Set up a series of automated emails for this campaign.'

  return (
    <Modal
      open={isOpen}
      title={modalTitle}
      description={modalDescription}
      onOpenChange={(open) => !open && onClose()}
      loading={isSubmitting}
      blockAccept={!canSubmit}
      onAccept={onSubmit}
      acceptButtonText="Create Sequence"
      size="xl"
    >
      <div className="space-y-6">
        <TextInput
          name="title"
          control={control}
          label="Sequence Title"
          placeholder="Enter sequence title"
          rules={{ required: 'Title is required' }}
        />

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Email Steps</h3>
            <FbButton size="sm" variant="secondary" onClick={addStep}>
              Add Step
            </FbButton>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <SequenceStepFormItem
                key={field.id}
                field={field}
                index={index}
                control={control}
                canRemove={fields.length > 1}
                isLast={index === fields.length - 1}
                onRemove={() => remove(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}
